import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ShieldCheck, ArrowRight, CheckCircle } from '@phosphor-icons/react';
import { submitLeadWithGeoLocation, getCountryInfo } from '../services/centralLeadApi';
import { toast } from 'sonner';
import countryCodes from '../data/countryCodes';

const HeroSection = ({ onOpenModal }) => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', description: '' });
  const [countryCode, setCountryCode] = useState('+1');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({ email: '', phone: '' });
  const [detectedCountry, setDetectedCountry] = useState(null);

  useEffect(() => {
    const fetchGeo = async () => {
      try {
        const info = await getCountryInfo();
        if (info) {
          setDetectedCountry(info);
          const match = countryCodes.find(c => c.country === info.countryCode);
          if (match) setCountryCode(match.code);
        }
      } catch (e) { console.error('Geo fetch error:', e); }
    };
    fetchGeo();
  }, []);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => phone.replace(/[^\d]/g, '').length === 10;

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^0-9\s\-]/g, '');
    const digits = value.replace(/[^\d]/g, '');
    if (digits.length > 10) return;
    setFormData({ ...formData, phone: value });
    if (errors.phone && digits.length === 10) setErrors(prev => ({ ...prev, phone: '' }));
  };

  const handleEmailChange = (e) => {
    setFormData({ ...formData, email: e.target.value });
    if (errors.email && validateEmail(e.target.value)) setErrors(prev => ({ ...prev, email: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = { email: '', phone: '' };
    let hasErrors = false;
    if (!validateEmail(formData.email)) { newErrors.email = 'Please enter a valid email (e.g., user@example.com)'; hasErrors = true; }
    if (!validatePhone(formData.phone)) { newErrors.phone = 'Phone number must be exactly 10 digits'; hasErrors = true; }
    if (hasErrors) { setErrors(newErrors); toast.error('Please fix the errors before submitting'); return; }

    setIsSubmitting(true);
    try {
      const leadData = {
        name: formData.name, email: formData.email, phone: formData.phone,
        countryCode: countryCode,
        country: countryCodes.find(c => c.code === countryCode)?.country || 'US',
        description: formData.description || 'Hero Form Submission'
      };
      const result = await submitLeadWithGeoLocation(leadData, null, 'Home Hero Section');
      if (result.success) {
        toast.success(result.message || 'Request submitted! Our team will contact you shortly.');
        setFormData({ name: '', email: '', phone: '', description: '' });
      } else {
        toast.error(result.message || 'Submission failed. Please try again.');
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Hero form submission error:', error);
      toast.error('Submission failed. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <section data-testid="hero-section" className="relative min-h-[90vh] flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1758518731706-be5d5230e5a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHRlYW0lMjBtZWV0aW5nJTIwcHJvZmVzc2lvbmFsJTIwZGlzY3Vzc2lvbnxlbnwwfHx8fDE3NzUwNjY3Nzl8MA&ixlib=rb-4.1.0&q=85" 
          alt="" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/40"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 w-full">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div className="pt-4">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#D4AF37] mb-6">
              Trusted by 10,000+ Businesses Nationwide
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold text-white tracking-tighter leading-[1.05] mb-6">
              Federal Trademark<br />
              Protection from <span className="text-[#D4AF37]">$45</span>
            </h1>
            <p className="text-base text-slate-300 leading-relaxed max-w-lg mb-10">
              Secure exclusive nationwide rights to your brand name, logo, and slogan. Licensed IP attorneys handle every step from comprehensive search to USPTO registration.
            </p>

            <div className="space-y-4 mb-10">
              {['98% USPTO approval rate across 10,000+ filings', 'Licensed IP attorneys with 15+ years experience', '24-48 hour expedited filing available'].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-[#D4AF37] flex-shrink-0" weight="bold" />
                  <span className="text-sm text-slate-200 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div data-testid="hero-lead-form" className="backdrop-blur-xl bg-white/95 border border-white/20 shadow-[0_20px_60px_rgba(0,0,0,0.3)] rounded-lg p-8 lg:p-10">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-heading font-bold text-slate-900 mb-1">Build a Stronger Brand Through Trademark Protection</h2>
                {/* <p className="text-sm text-[#044B3B] font-semibold">Free Legal Consultation</p> */}
              </div>
             
            </div>

            <p className="text-sm text-slate-500 mb-6 leading-relaxed">
              Take the first step toward securing your business name, logo, and intellectual property with trusted trademark registration services.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input data-testid="hero-name-input" type="text" placeholder="Full Name *" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full border border-slate-200 focus:border-[#044B3B] rounded-md px-4 py-3 text-sm" required />

              <div>
                <Input data-testid="hero-email-input" type="email" placeholder="Email Address *" value={formData.email} onChange={handleEmailChange}
                  onBlur={() => { if (formData.email && !validateEmail(formData.email)) setErrors(prev => ({ ...prev, email: 'Please enter a valid email' })); else setErrors(prev => ({ ...prev, email: '' })); }}
                  className={`w-full border ${errors.email ? 'border-red-500' : 'border-slate-200'} focus:border-[#044B3B] rounded-md px-4 py-3 text-sm`} required />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              <div>
                <div className="flex items-stretch gap-2">
                  <Select value={countryCode} onValueChange={setCountryCode}>
                    <SelectTrigger data-testid="hero-country-select" className="w-24 h-10 px-2 border border-slate-200 rounded-md bg-white text-xs font-medium text-slate-700 focus:border-[#044B3B]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[200px]">
                      {countryCodes.map((country, index) => (
                        <SelectItem key={index} value={country.code}>{country.flag} {country.code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input data-testid="hero-phone-input" type="tel" placeholder="Phone Number *" value={formData.phone} onChange={handlePhoneChange}
                    onBlur={() => { const digits = formData.phone.replace(/[^\d]/g, ''); if (formData.phone && digits.length !== 10) setErrors(prev => ({ ...prev, phone: 'Phone must be 10 digits' })); else setErrors(prev => ({ ...prev, phone: '' })); }}
                    className={`flex-1 border ${errors.phone ? 'border-red-500' : 'border-slate-200'} focus:border-[#044B3B] rounded-md px-4 py-3 text-sm`} required />
                </div>
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>

              <Textarea data-testid="hero-description-input" placeholder="What are you looking to protect? (e.g. Name, Logo and Slogan)" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full border border-slate-200 focus:border-[#044B3B] rounded-md px-4 py-3 text-sm resize-none" rows={3} required />

              <Button data-testid="hero-submit-btn" type="submit" disabled={isSubmitting}
                className="w-full bg-[#044B3B] hover:bg-[#03392D] text-white font-semibold text-sm py-6 rounded-md">
                {isSubmitting ? 'Submitting...' : (
                  <span className="flex items-center justify-center gap-2">
                    Register Your Trademark <ArrowRight className="h-4 w-4" />
                  </span>
                )}
              </Button>
            </form>
            <p className="text-xs text-slate-400 text-center mt-4">No obligation. Secure & confidential. Expert advice.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
